/**
 * Real-Time Data Synchronization Service
 * Provides instant updates across all components and roles when data changes
 */

export interface DataChangeEvent {
  type: 'property' | 'room' | 'client' | 'rmc' | 'master-data' | 'booking' | 'user' | 'amenity' | 'facility';
  action: 'create' | 'update' | 'delete';
  entityId: string | number;
  data?: any;
  affectedEntities?: {
    type: string;
    ids: (string | number)[];
  }[];
  timestamp: number;
  userId?: string;
  userRole?: string;
}

export interface SyncListener {
  id: string;
  callback: (event: DataChangeEvent) => void;
  filters?: {
    types?: string[];
    actions?: string[];
    entityIds?: (string | number)[];
  };
}

class RealTimeSyncService {
  private listeners: Map<string, SyncListener> = new Map();
  private eventQueue: DataChangeEvent[] = [];
  private isProcessing = false;

  /**
   * Subscribe to real-time data changes
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.set(listener.id, listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener.id);
    };
  }

  /**
   * Broadcast data change to all subscribers
   */
  async broadcast(event: DataChangeEvent) {
    // Add timestamp if not provided
    if (!event.timestamp) {
      event.timestamp = Date.now();
    }

    // Add to queue for processing
    this.eventQueue.push(event);
    
    if (!this.isProcessing) {
      this.processEventQueue();
    }
  }

  /**
   * Process the event queue
   */
  private async processEventQueue() {
    if (this.isProcessing || this.eventQueue.length === 0) return;

    this.isProcessing = true;

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()!;
      await this.processEvent(event);
    }

    this.isProcessing = false;
  }

  /**
   * Process individual event
   */
  private async processEvent(event: DataChangeEvent) {
    console.log('🔄 Broadcasting real-time update:', event);

    this.listeners.forEach(async (listener, listenerId) => {
      try {
        // Apply filters if specified
        if (this.shouldNotifyListener(listener, event)) {
          await listener.callback(event);
        }
      } catch (error) {
        console.error(`Error notifying listener ${listenerId}:`, error);
      }
    });
  }

  /**
   * Check if listener should be notified based on filters
   */
  private shouldNotifyListener(listener: SyncListener, event: DataChangeEvent): boolean {
    const { filters } = listener;
    
    if (!filters) return true;

    // Check type filter
    if (filters.types && !filters.types.includes(event.type)) {
      return false;
    }

    // Check action filter
    if (filters.actions && !filters.actions.includes(event.action)) {
      return false;
    }

    // Check entity ID filter
    if (filters.entityIds && !filters.entityIds.includes(event.entityId)) {
      return false;
    }

    return true;
  }

  /**
   * Broadcast property changes
   */
  async broadcastPropertyChange(
    action: 'create' | 'update' | 'delete',
    propertyId: string | number,
    propertyData?: any
  ) {
    await this.broadcast({
      type: 'property',
      action,
      entityId: propertyId,
      data: propertyData,
      affectedEntities: [
        { type: 'room', ids: propertyData?.roomIds || [] },
        { type: 'property', ids: [propertyId] }
      ],
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast room changes
   */
  async broadcastRoomChange(
    action: 'create' | 'update' | 'delete',
    roomId: string | number,
    roomData?: any
  ) {
    await this.broadcast({
      type: 'room',
      action,
      entityId: roomId,
      data: roomData,
      affectedEntities: [
        { type: 'room', ids: [roomId] },
        { type: 'property', ids: roomData?.propertyId ? [roomData.propertyId] : [] }
      ],
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast client changes
   */
  async broadcastClientChange(
    action: 'create' | 'update' | 'delete',
    clientId: string | number,
    clientData?: any
  ) {
    await this.broadcast({
      type: 'client',
      action,
      entityId: clientId,
      data: clientData,
      affectedEntities: [
        { type: 'client', ids: [clientId] },
        { type: 'rmc', ids: clientData?.rmcId ? [clientData.rmcId] : [] }
      ],
      timestamp: Date.now()
    });
  }

  /**
   * Broadcast master data changes
   */
  async broadcastMasterDataChange(
    subType: 'cities' | 'room-types' | 'property-types',
    action: 'create' | 'update' | 'delete',
    entityId: string | number,
    data?: any
  ) {
    await this.broadcast({
      type: 'master-data',
      action,
      entityId: `${subType}:${entityId}`,
      data: { subType, ...data },
      timestamp: Date.now()
    });
  }

  /**
   * Get active listeners count
   */
  getListenersCount(): number {
    return this.listeners.size;
  }

  /**
   * Get listener info for debugging
   */
  getListenersInfo(): { id: string; hasFilters: boolean }[] {
    const info: { id: string; hasFilters: boolean }[] = [];
    this.listeners.forEach((listener, id) => {
      info.push({
        id: id,
        hasFilters: !!listener.filters
      });
    });
    return info;
  }

  /**
   * Clear all listeners (useful for cleanup)
   */
  clear() {
    this.listeners.clear();
    this.eventQueue.length = 0;
    this.isProcessing = false;
  }
}

// Export singleton instance
export const realTimeSyncService = new RealTimeSyncService();

// Helper function to generate unique listener IDs
export function generateListenerId(componentName: string, instanceId?: string): string {
  const id = instanceId || Math.random().toString(36).substring(2, 9);
  return `${componentName}_${id}`;
}
